/**
 * O QUE VAI DENTRO DO QR COLADO NA MÁQUINA.
 *
 * Pedido do dono (26/10/2026): *"vamos criar um QR Code com o código do
 * sistema, aí a galera cola ele em adesivo nas máquinas, para fazer um
 * lançamento de combustível, abertura de apropriação de horas, abertura de
 * chamado de manutenção etc… assim já preenche o equipamento, eu zero a
 * chance de erro de preenchimento"*.
 *
 * O erro que isso mata é real e é SILENCIOSO: a frota tem BAS-001 a BAS-016,
 * caminhões idênticos, num campo de escolha. Pegar o vizinho manda o diesel
 * para a cadeia de consumo da máquina errada, estraga o L/h das DUAS, e o
 * alerta de fora-da-faixa dispara na inocente. O sistema não tem como
 * perceber: só o estorno conserta, e só se alguém notar.
 *
 * ## O que vai dentro, e por que mudou (decisão 64, 31/10/2026)
 *
 * O QR leva o CÓDIGO DO SISTEMA, nu, exatamente como vai impresso e em caixa
 * alta: `BAS-001`. É a decisão do dono ao pedir o arquivo de identificação
 * (31/10/2026): *"o código tem que ser o código do sistema [...] importante:
 * os QR também são o código"*. E ele pediu também adesivo para máquina que
 * AINDA NÃO EXISTE no sistema ("tenho até BAS-03, posso escolher fazer mais 3
 * e ir até BAS-06"): máquina sem cadastro não tem `id`, e o adesivo dela só
 * pode levar o código.
 *
 * A primeira versão (26/10/2026) levava `CTF1:EQ:<id>`, e o porquê dela
 * continua verdadeiro, só que agora é a CONSEQUÊNCIA aceita, e não o motivo
 * de recusa: o código é único só dentro da empresa, e se um dia ele mudar
 * (correção de cadastro, importação), o adesivo impresso continua com o
 * código antigo e passa a apontar para quem herdar esse código, ou para
 * nada. Hoje o ajuste da ficha não muda o código (0131), e a ficha avisa que
 * o adesivo leva o código. Código que mudar pede adesivo novo.
 *
 * Três coisas continuam iguais:
 *
 * 1. NÃO É URL. Adesivo é o lugar mais caro do mundo para gravar um endereço:
 *    o dono avisou que o domínio de hoje não é eterno, e `H12` proíbe nome de
 *    máquina dentro do código. O que vai aqui é IDENTIFICADOR.
 *
 * 2. O ADESIVO ANTIGO CONTINUA LENDO. `CTF1:EQ:<id>` já foi impresso e pode
 *    estar colado: o leitor aceita os dois formatos, para sempre. O código nu
 *    não tem esquema nem versão; ele é reconhecido pela FORMA (letras,
 *    algarismos e traço, de 2 a 20, com pelo menos uma letra), e por isso o
 *    formato antigo, com dois-pontos, nunca é confundido com ele.
 *
 * 3. O código vai também IMPRESSO, grande. Sol, diesel e barro acabam com QR,
 *    e aí o humano lê e digita.
 *
 * O TIPO (`EQ`) do formato antigo continua reservado: é onde a folha impressa
 * entra quando ganhar o QR que a decisão 44 adiou (`CTF1:FOLHA:<id>`).
 *
 * ## O que o QR NÃO é
 *
 * Não é credencial. Adesivo é público — qualquer um fotografa no pátio. Ele
 * diz QUAL máquina, nunca QUEM pode. Escanear continua exigindo sessão e
 * permissão; sem isso, quem passasse pelo pátio lançaria abastecimento.
 */

/** O esquema e a versão do formato antigo (`CTF1:EQ:<id>`), que continua lendo. */
const ESQUEMA = 'CTF1';

/** O que o adesivo aponta. `FOLHA` entra com a folha impressa (decisão 44). */
export type TipoDoQr = 'equipamento' | 'codigo';

const TIPOS: Readonly<Record<string, 'equipamento'>> = { EQ: 'equipamento' };

/** O `id` do banco: oito-quatro-quatro-quatro-doze, em hexadecimal. */
const FORMATO_DO_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A forma de um código de máquina (decisão 64): letras, algarismos e traço,
 * de 2 a 20, começando e terminando em letra ou algarismo, e com pelo menos
 * uma letra. A letra obrigatória tira da leitura o QR que é só número (o da
 * nota, o do boleto), e o traço só no meio tira a sujeira das bordas.
 */
const FORMATO_DO_CODIGO = /^(?=.{2,20}$)(?=.*[A-Z])[A-Z0-9](?:[A-Z0-9-]*[A-Z0-9])?$/;

/** O adesivo antigo (`CTF1:EQ:<id>`) ou o de hoje (o código nu). */
export type QrLido =
  | { readonly tipo: 'equipamento'; readonly id: string }
  | { readonly tipo: 'codigo'; readonly codigo: string };

/**
 * O código como ele vai no QR e no papel: sem espaço nas pontas e em caixa
 * alta. `null` quando não tem a forma de um código de máquina, e aí não há
 * adesivo para imprimir.
 */
export function conteudoDoQr(codigo: string): string | null {
  const limpo = codigo.trim().toUpperCase();
  return FORMATO_DO_CODIGO.test(limpo) ? limpo : null;
}

/**
 * Lê o conteúdo de um QR qualquer. `null` quando não é deste sistema.
 *
 * TOLERANTE NA CASCA, ESTRITO NO MIOLO: espaço nas pontas e caixa não
 * importam, a câmera lê o que o papel tem, e papel amassado sob sol gera
 * leitura com sujeira nas bordas. O `id` do formato antigo, esse, ou casa com
 * o formato ou não é lido: aceitar `id` torto faria a tela pedir ao servidor
 * uma máquina que não existe e mostrar "não encontrada" no lugar de "este QR
 * não é de uma máquina".
 */
export function lerQr(texto: string): QrLido | null {
  const limpo = texto.trim();
  if (!limpo.includes(':')) {
    const codigo = conteudoDoQr(limpo);
    return codigo === null ? null : { tipo: 'codigo', codigo };
  }
  const partes = limpo.split(':');
  if (partes.length !== 3) return null;
  const [esquema, tipo, id] = partes as [string, string, string];
  if (esquema.toUpperCase() !== ESQUEMA) return null;
  const doTipo = TIPOS[tipo.toUpperCase()];
  if (doTipo === undefined) return null;
  if (!FORMATO_DO_ID.test(id)) return null;
  return { tipo: doTipo, id: id.toLowerCase() };
}

/** `true` quando o texto lido é QR de outro sistema, e não sujeira de leitura. */
export function ehQrDeOutroSistema(texto: string): boolean {
  const limpo = texto.trim();
  return limpo.length > 0 && lerQr(limpo) === null;
}

/** A máquina da frota que o código lido aponta, sem diferença de caixa. */
export function maquinaDoCodigo<T extends { readonly codigo: string }>(frota: readonly T[], codigo: string): T | undefined {
  const procurado = codigo.trim().toUpperCase();
  return frota.find((m) => m.codigo.trim().toUpperCase() === procurado);
}
